import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Send, CheckCircle2, ArrowRight, LogIn, Mail, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import DisclosureGate from "@/components/DisclosureGate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showDisclosure, setShowDisclosure] = useState(false);
  const [disclosureCompleted, setDisclosureCompleted] = useState(false);
  const [disclosureChecked, setDisclosureChecked] = useState(false);
  const registrationRequired = location.state?.registrationRequired === true;
  const consultationRequest = location.state?.consultationRequest === true;
  const prefillEmail = location.state?.prefillEmail || "";
  const prefillName = location.state?.prefillName || "";

  useEffect(() => {
    const stored = localStorage.getItem("disclosure_acknowledged");
    if (stored) setDisclosureCompleted(true);
  }, []);
  const [formData, setFormData] = useState({
    name: prefillName,
    email: prefillEmail,
    phone: "",
    property_sold_price: "",
    exchange_deadline: "",
    target_market: "",
    budget: "",
    notes: consultationRequest
      ? "I'd like to schedule a consultation to discuss my 1031 exchange options and replacement-property criteria."
      : ""
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email) {
      toast.error("Please fill in your name and email");
      return;
    }
    if (!disclosureCompleted) {
      setShowDisclosure(true);
      return;
    }
    submitForm();
  };

  const submitForm = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        ...formData,
        investment_budget: formData.budget,
        source: "contact_form"
      });
      toast.success("Inquiry submitted successfully!");
      setSubmitted(true);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      // Surface the real reason so the user (and support) can act on it
      let msg = "Failed to submit. Please try again.";
      if (status === 422 && detail) {
        msg = Array.isArray(detail)
          ? `Please check: ${detail.map((d) => d.msg || d.loc?.slice(-1)[0]).join(", ")}`
          : `Validation error: ${detail}`;
      } else if (typeof detail === "string") {
        msg = detail;
      } else if (!err.response) {
        msg = "Network error — please check your connection and try again.";
      } else if (status >= 500) {
        msg = "Server error — we've been notified. Please try again in a moment.";
      }
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            className="max-w-3xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="overline mb-4 block">{consultationRequest ? "Consultation Request" : "Get in Touch"}</span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-light tracking-tight text-[#1A1A2E] leading-[1.1] mb-6" data-testid="contact-title">
              {consultationRequest ? "Request a Consultation" : "Contact Us"}
            </h1>
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed mb-4">
              {consultationRequest
                ? "Tell us a bit about your exchange and a licensed broker will follow up within one business day to walk through your options."
                : "Whether you have a question about the 1031 exchange process or would like to discuss your property criteria with a broker, we're here to help."}
            </p>
            <p className="text-sm text-slate-500">
              By submitting this form, you are requesting information about available condo units. No brokerage relationship is formed until a written agreement is executed.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-24 bg-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-3 gap-16">
            {/* Form */}
            <motion.div
              className="lg:col-span-2"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              {submitted ? (
                <motion.div
                  className="py-10"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  data-testid="contact-success-panel"
                >
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 className="w-8 h-8 text-green-600" />
                  </div>
                  <h2 className="text-3xl font-light text-[#1A1A2E] mb-3 text-center">You're Registered</h2>
                  <p className="text-slate-600 text-center max-w-md mx-auto mb-10">
                    Thanks for reaching out. A member of our team will contact you within
                    one business day to discuss your 1031 exchange and upcoming property criteria.
                  </p>

                  <div className="max-w-xl mx-auto bg-white border border-slate-200 rounded-xl p-6 mb-8">
                    <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-4">
                      What happens next
                    </p>
                    <ol className="space-y-4">
                      <li className="flex gap-4">
                        <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#0066FF]/10 text-[#0066FF] font-semibold flex items-center justify-center text-sm">1</span>
                        <div>
                          <p className="font-medium text-[#1A1A2E] flex items-center gap-2">
                            <LogIn className="w-4 h-4 text-slate-400" />
                            Sign in to the Client Portal
                          </p>
                          <p className="text-sm text-slate-600">
                            Use Google Sign-In with the email you just registered to unlock the full inventory, save portfolios, and build your 1031 shortlist.
                          </p>
                        </div>
                      </li>
                      <li className="flex gap-4">
                        <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#0066FF]/10 text-[#0066FF] font-semibold flex items-center justify-center text-sm">2</span>
                        <div>
                          <p className="font-medium text-[#1A1A2E] flex items-center gap-2">
                            <Mail className="w-4 h-4 text-slate-400" />
                            Check your email
                          </p>
                          <p className="text-sm text-slate-600">
                            We've sent a welcome note with next steps and a copy of our 1031 exchange guide.
                          </p>
                        </div>
                      </li>
                      <li className="flex gap-4">
                        <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#0066FF]/10 text-[#0066FF] font-semibold flex items-center justify-center text-sm">3</span>
                        <div>
                          <p className="font-medium text-[#1A1A2E] flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-slate-400" />
                            Expect our call
                          </p>
                          <p className="text-sm text-slate-600">
                            A licensed broker will reach out within 1 business day to understand your timeline and match you to replacement properties.
                          </p>
                        </div>
                      </li>
                    </ol>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Link to="/portal">
                      <Button
                        className="btn-primary px-8 py-5 rounded-sm w-full sm:w-auto"
                        data-testid="contact-success-login-btn"
                      >
                        <LogIn className="mr-2 w-4 h-4" />
                        Sign In to Client Portal
                      </Button>
                    </Link>
                    <Link to="/resources">
                      <Button
                        variant="outline"
                        className="btn-secondary px-8 py-5 rounded-sm w-full sm:w-auto"
                        data-testid="contact-success-resources-btn"
                      >
                        Read the 1031 Guide
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </Link>
                  </div>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-8 bg-white border border-slate-200 rounded-xl p-8 shadow-sm">
                  {consultationRequest && (
                    <div
                      className="p-4 border-l-4 border-[#0066FF] bg-blue-50 rounded-r-lg"
                      data-testid="consultation-request-banner"
                    >
                      <p className="font-medium text-[#1A1A2E] text-sm mb-1">
                        Consultation requested
                      </p>
                      <p className="text-sm text-slate-600">
                        We've pre-filled your details below. Confirm your contact info and add anything you'd like the broker to know — they'll reach out within 1 business day.
                      </p>
                    </div>
                  )}
                  {registrationRequired && (
                    <div
                      className="p-4 border-l-4 border-[#0066FF] bg-blue-50 rounded-r-lg"
                      data-testid="registration-required-banner"
                    >
                      <p className="font-medium text-[#1A1A2E] text-sm mb-1">
                        One quick step before you sign in
                      </p>
                      <p className="text-sm text-slate-600">
                        Complete this brief intake form so a licensed broker can
                        follow up, then return to the Client Portal to sign in with the same email.
                        {prefillEmail && <> We've pre-filled <strong>{prefillEmail}</strong> below.</>}
                      </p>
                    </div>
                  )}
                  {/* Contact Info */}
                  <div>
                    <h2 className="text-xl font-medium text-[#1A1A2E] mb-4">Your Information</h2>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Full Name *</label>
                        <Input
                          type="text"
                          value={formData.name}
                          onChange={(e) => handleChange("name", e.target.value)}
                          className="h-12 rounded-sm"
                          placeholder="Your full name"
                          required
                          data-testid="contact-name-input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Email Address *</label>
                        <Input
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleChange("email", e.target.value)}
                          className="h-12 rounded-sm"
                          placeholder="your@email.com"
                          required
                          data-testid="contact-email-input"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-sm font-medium text-slate-700 mb-2">Phone Number</label>
                        <Input
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => handleChange("phone", e.target.value)}
                          className="h-12 rounded-sm"
                          placeholder="(555) 555-5555"
                          data-testid="contact-phone-input"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Exchange Details */}
                  <div>
                    <h2 className="text-xl font-medium text-white mb-4">Exchange Details <span className="text-sm font-normal text-white/40">(optional)</span></h2>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Property Sale Price</label>
                        <Select value={formData.property_sold_price} onValueChange={(v) => handleChange("property_sold_price", v)}>
                          <SelectTrigger className="h-12 rounded-sm" data-testid="exchange-price-select">
                            <SelectValue placeholder="Select range" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="under-1m">Under $1 Million</SelectItem>
                            <SelectItem value="1m-3m">$1M - $3 Million</SelectItem>
                            <SelectItem value="3m-5m">$3M - $5 Million</SelectItem>
                            <SelectItem value="5m-10m">$5M - $10 Million</SelectItem>
                            <SelectItem value="over-10m">Over $10 Million</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Exchange Timeline</label>
                        <Select value={formData.exchange_deadline} onValueChange={(v) => handleChange("exchange_deadline", v)}>
                          <SelectTrigger className="h-12 rounded-sm" data-testid="exchange-timeline-select">
                            <SelectValue placeholder="Select timeline" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="already-sold">Already Sold (Clock Running)</SelectItem>
                            <SelectItem value="under-contract">Under Contract</SelectItem>
                            <SelectItem value="1-3-months">Planning to Sell in 1-3 Months</SelectItem>
                            <SelectItem value="3-6-months">Planning to Sell in 3-6 Months</SelectItem>
                            <SelectItem value="exploring">Just Exploring</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Target Market</label>
                        <Select value={formData.target_market} onValueChange={(v) => handleChange("target_market", v)}>
                          <SelectTrigger className="h-12 rounded-sm" data-testid="target-market-select">
                            <SelectValue placeholder="Select market" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="nyc">New York City</SelectItem>
                            <SelectItem value="miami">Miami</SelectItem>
                            <SelectItem value="both">Both Markets</SelectItem>
                            <SelectItem value="undecided">Undecided</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Budget</label>
                        <Select value={formData.budget} onValueChange={(v) => handleChange("budget", v)}>
                          <SelectTrigger className="h-12 rounded-sm" data-testid="budget-select">
                            <SelectValue placeholder="Select budget" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="under-1m">Under $1 Million</SelectItem>
                            <SelectItem value="1m-2m">$1M - $2 Million</SelectItem>
                            <SelectItem value="2m-5m">$2M - $5 Million</SelectItem>
                            <SelectItem value="5m-10m">$5M - $10 Million</SelectItem>
                            <SelectItem value="over-10m">Over $10 Million</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Message</label>
                    <Textarea
                      value={formData.notes}
                      onChange={(e) => handleChange("notes", e.target.value)}
                      className="rounded-sm min-h-[120px]"
                      placeholder="Tell us about your situation, questions, or what you're looking for..."
                      data-testid="notes-textarea"
                    />
                  </div>

                  {/* Disclosure Acknowledgment */}
                  <label className="flex items-start gap-3 cursor-pointer" data-testid="contact-disclosure-checkbox-label">
                    <input
                      type="checkbox"
                      checked={disclosureChecked}
                      onChange={(e) => setDisclosureChecked(e.target.checked)}
                      className="mt-1 w-4 h-4 rounded border-gray-300 text-[#0066FF] focus:ring-[#0066FF]"
                      data-testid="contact-disclosure-checkbox"
                    />
                    <span className="text-sm text-slate-600">
                      I acknowledge receipt of required brokerage disclosure information.
                      <span className="block text-xs text-slate-400 mt-1">Required prior to discussing specific properties, pricing, or transaction terms.</span>
                    </span>
                  </label>

                  <Button
                    type="submit"
                    className="btn-primary px-8 py-5 rounded-sm"
                    disabled={loading || !disclosureChecked}
                    data-testid="contact-submit-btn"
                  >
                    <Send className="mr-2 w-4 h-4" />
                    {loading ? "Submitting..." : "Request Information"}
                  </Button>
                </form>
              )}
            </motion.div>

            {/* Sidebar */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
            >
            </motion.div>
          </div>
        </div>
      </section>

      {/* Brokerage Statement */}
      <div className="py-8 bg-slate-50 border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <p className="text-xs text-slate-500 leading-relaxed text-center">
            Krieger Real Estate LLC acts solely as a real estate broker representing buyers and/or sellers in property transactions. Licensed in New York and Florida.
          </p>
        </div>
      </div>

      <DisclosureGate
        open={showDisclosure}
        onComplete={() => {
          setShowDisclosure(false);
          setDisclosureCompleted(true);
          submitForm();
        }}
        onClose={() => setShowDisclosure(false)}
      />
    </div>
  );
}
